<template>
  <div
    class="field-wrapper"
    :class="{ 'has-errors': errors.length }"
  >
    <label
      v-if="label"
      :for="name"
    >
      {{ label }}
    </label>
    <input
      :id="name"
      :name="name"
      :placeholder="placeholder"
      :value="value"
      :disabled="disabled"
      :aria-label="label ? undefined : placeholder"
      @input="onInput"
    />
    <div
      v-for="error of errors"
      :key="error.$uid"
      class="input-errors"
    >
      <div class="error-msg">{{ $t(`form.${name}.errors.${error.$validator}`) }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { ErrorObject } from '@vuelidate/core'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()

  interface Props {
    label?: string
    name: string
    placeholder: string
    value?: string | number | null
    optional?: boolean
    errors?: ErrorObject[]
    disabled?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    label: '',
    value: null,
    optional: false,
    errors: () => [],
    disabled: false
  })

  const emits = defineEmits(['input'])

  const label = ref(props.label)

  if (props.optional) {
    label.value += t('reusable.optional')
  }

  const onInput = (event: Event) => {
    if (!props.disabled) {
      const target = event.target as HTMLInputElement

      emits('input', { name: props.name, value: target.value })
    }
  }
</script>

<style lang="scss" scoped>
  @import '~/assets/styles/common.scss';

  .field-wrapper {
    display: flex;
    flex-direction: column;
    margin-bottom: 24px;

    &.has-errors {
      input {
        border-color: $error;

        &:focus {
          outline: 1px solid $error;
        }
      }
    }

    .input-errors {
      margin-top: 8px;

      .error-msg {
        color: $error;
        font-size: rem(13);
        line-height: 16px;
      }
    }

    label {
      font-size: rem(13);
      line-height: 16px;
      color: $bt-blue;
      margin-bottom: 8px;
    }

    input {
      height: 48px;
      border-radius: 4px;
      border: 1px solid $form-field-border;
      box-shadow: 0px 4px 10px rgba(white, 0.62);
      padding: 0 16px;
      font-size: 16px;
      line-height: 16px;
      color: $bt-blue;

      &::placeholder {
        color: $secondary;
      }

      &:focus {
        outline: 1px solid $bt-blue;
      }
    }

    .input-errors {
      margin-top: 8px;

      .error-msg {
        color: $error;
        font-size: rem(13);
        line-height: 16px;
      }
    }
  }
</style>
